import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LayoutWrapper from '../../../../shared/components/LayoutWrapper';
import SmartTable, { SmartTableColumn } from '../../../../shared/components/SmartTable';
import SmartTableButtons from '../../../../shared/components/SmartTableButtons';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { API_CONFIG } from '../../../../config/api';

interface SummaryRow {
  Oid: string;
  CustomerID?: string;
  CustomerName?: string;
  YearType?: number;
  CityTaxSalesAmount?: number;
  VATPayableAmount?: number;
  NonVATSalesAmount?: number;
  VATInclusiveSalesAmount?: number;
  CityTaxPurchaseAmount?: number;
  VATReceivableAmount?: number;
  NonVATPurchaseAmount?: number;
  VATInclusivePurchaseAmount?: number;
  TaxableCityTax?: number;
  VATDifference?: number;
  AmountIncomeTaxPayable?: number;
  TaxableIncomeTax?: number;  
  CreateDate?: string;
  [key: string]: any;
}

const formatNumber = (value: any) => {
  if (value === null || value === undefined) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value: any) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
};

const renderWithLedgerHighlight = (key: string, formatter?: (v: any) => any) => (value: any, row: any) => {
  let display = formatter ? formatter(value) : value;
  if (!formatter && key.toLowerCase().includes('date')) {
    display = formatDate(value);
  }
  if (display === null || display === undefined || display === '') display = '-';
  if (row?.IsLedger) {
    return <span style={{ color: '#10B981', fontWeight: 600 }}>{display}</span>;
  }
  return display;
};

const SummaryYearPage: React.FC = () => {
  const { userAuth } = useAuth();
  
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState('2025');
  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  
  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
      
    try {
      const token = localStorage.getItem('novaqToken');
      if (!token) {
        throw new Error('NovaQ token олдсонгүй. Дахин нэвтэрнэ үү.');
      }

      const queryYear = year || '';
      const url = `${API_CONFIG.BASE_URL}/api/taxsystem/ebarimt/accountant/receipts/summary-year?year=${encodeURIComponent(queryYear)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Серверийн алдаа: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success) {
        setRows(result.data || []);
      } else {
        throw new Error(result?.message || 'Өгөгдөл татахад алдаа гарлаа');
      }
    } catch (err: any) {
      console.error('❌ Summary period fetch error:', err);
      setError(err?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filteredRows = useMemo(() => {
    const idTerm = customerIdFilter.trim().toLowerCase();
    const nameTerm = customerNameFilter.trim().toLowerCase();
    return rows.filter(r => {
      const idMatch = idTerm ? (r.CustomerID || '').toString().toLowerCase().includes(idTerm) : true;
      const nameMatch = nameTerm ? (r.CustomerName || '').toLowerCase().includes(nameTerm) : true;
      return idMatch && nameMatch;
    });
  }, [rows, customerIdFilter, customerNameFilter]);

  const columns: SmartTableColumn[] = useMemo(
    () => [
      { key: 'CustomerID', label: 'Харилцагчийн регистр', width: 160, render: renderWithLedgerHighlight('CustomerID') },
      { key: 'CustomerName', label: 'Харилцагчийн нэр', width: 200, render: renderWithLedgerHighlight('CustomerName') },
      { key: 'YearType', label: 'Он', width: 80, render: renderWithLedgerHighlight('YearType') },
      { key: 'CityTaxSalesAmount', label: 'НХАТ борлуулалт', width: 140, render: renderWithLedgerHighlight('CityTaxSalesAmount', formatNumber) },
      { key: 'VATPayableAmount', label: 'Төлөх НӨАТ', width: 140, render: renderWithLedgerHighlight('VATPayableAmount', formatNumber) },
      { key: 'NonVATSalesAmount', label: 'НӨАТ-гүй борл.', width: 140, render: renderWithLedgerHighlight('NonVATSalesAmount', formatNumber) },
      { key: 'VATInclusiveSalesAmount', label: 'НӨАТ-тэй борл.', width: 140, render: renderWithLedgerHighlight('VATInclusiveSalesAmount', formatNumber) },
      { key: 'CityTaxPurchaseAmount', label: 'Хотын татв. худ.', width: 140, render: renderWithLedgerHighlight('CityTaxPurchaseAmount', formatNumber) },
      { key: 'VATReceivableAmount', label: 'Буцаах НӨАТ', width: 140, render: renderWithLedgerHighlight('VATReceivableAmount', formatNumber) },
      { key: 'NonVATPurchaseAmount', label: 'НӨАТ-гүй худ.', width: 140, render: renderWithLedgerHighlight('NonVATPurchaseAmount', formatNumber) },
      { key: 'VATInclusivePurchaseAmount', label: 'НӨАТ-тэй худ.', width: 140, render: renderWithLedgerHighlight('VATInclusivePurchaseAmount', formatNumber) },
      { key: 'TaxableCityTax', label: 'Төлөх НХАТ', width: 160, render: renderWithLedgerHighlight('TaxableCityTax', formatNumber) },
      { key: 'VATDifference', label: 'НӨАТ зөрүү', width: 140, render: renderWithLedgerHighlight('VATDifference', formatNumber) },
      { key: 'AmountIncomeTaxPayable', label: 'ОАТ ноогдуулах дүн', width: 160, render: renderWithLedgerHighlight('AmountIncomeTaxPayable', formatNumber) },
      { key: 'TaxableIncomeTax', label: 'Ноогдсон ОАТ', width: 160, render: renderWithLedgerHighlight('TaxableIncomeTax', formatNumber) },
     
      { key: 'CreateDate', label: 'Үүсгэсэн огноо', width: 160, render: renderWithLedgerHighlight('CreateDate', formatDate) },
    ],
    []
  );

  const footerData = useMemo(() => {
    const sum = (key: keyof SummaryRow) =>
      filteredRows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

    return {
      CityTaxSalesAmount: sum('CityTaxSalesAmount'),
      VATPayableAmount: sum('VATPayableAmount'),
      NonVATSalesAmount: sum('NonVATSalesAmount'),
      VATInclusiveSalesAmount: sum('VATInclusiveSalesAmount'),
      CityTaxPurchaseAmount: sum('CityTaxPurchaseAmount'),
      VATReceivableAmount: sum('VATReceivableAmount'),
      NonVATPurchaseAmount: sum('NonVATPurchaseAmount'),
      VATInclusivePurchaseAmount: sum('VATInclusivePurchaseAmount'),
      VATDifference: sum('VATDifference'),
      TaxableCityTax: sum('TaxableCityTax'),
      AmountIncomeTaxPayable: sum('AmountIncomeTaxPayable'),
      TaxableIncomeTax: sum('TaxableIncomeTax'),
      
    } as SummaryRow;
  }, [filteredRows]);

  const handleRefresh = () => fetchRows();

  const handleExportToExcel = () => {
    try {
      // Prepare CSV data
      const headers = columns.map(col => col.label);
      const csvRows: string[] = [];
      
      // Add headers
      csvRows.push(headers.join(','));
      
      // Add data rows
      filteredRows.forEach(row => {
        const values = columns.map(col => {
          const value = row[col.key];
          // Format numbers
          if (col.key.includes('Amount') || col.key.includes('Tax') || col.key === 'YearType') {
            return value !== null && value !== undefined ? formatNumber(value) : '';
          }
          // Format dates
          if (col.key.includes('Date')) {
            return formatDate(value);
          }
          // Escape commas and quotes in strings
          const stringValue = value !== null && value !== undefined ? String(value) : '';
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values.join(','));
      });
      
      // Add footer row if available
      if (footerData) {
        const footerValues = columns.map(col => {
          const value = footerData[col.key as keyof SummaryRow];
          if (col.key.includes('Amount') || col.key.includes('Tax')) {
            return value !== null && value !== undefined ? formatNumber(value) : '';
          }
          if (col.key === 'CustomerID' || col.key === 'CustomerName') {
            return 'Нийт';
          }
          return '';
        });
        csvRows.push(footerValues.join(','));
      }
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8 support
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Товчоо_жилээр_${year}_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('❌ Excel export error:', err);
      setError('Excel файл татахад алдаа гарлаа');
    }
  };

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Товчоо (жилээр)</h1>
          <div className="flex gap-2">
            <button
              onClick={handleExportToExcel}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
              title="Excel-д баримт татах"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel-д татах
            </button>
            <SmartTableButtons
              showColumnSearch={showColumnSearch}
              onToggleSearch={() => setShowColumnSearch((prev) => !prev)}
              onColumnSettings={() => setShowColumnSettings((prev) => !prev)}
              onRefresh={handleRefresh}
              addable={false}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-customer-id">
                Регистр
              </label>
              <input
                id="sales-customer-id"
                type="text"
                className="border rounded px-3 py-2 w-48"
                value={customerIdFilter}
                onChange={(e) => setCustomerIdFilter(e.target.value)}
                placeholder="РД хайх"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-customer-name">
                Нэр
              </label>
              <input
                id="sales-customer-name"
                type="text"
                className="border rounded px-3 py-2 w-56"
                value={customerNameFilter}
                onChange={(e) => setCustomerNameFilter(e.target.value)}
                placeholder="Нэр хайх"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1" htmlFor="sales-year">
                Он (year)
              </label>
              <input
                id="sales-year"
                type="number"
                className="border rounded px-3 py-2 w-32"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={fetchRows}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Шүүх
              </button>
            </div>
          </div>
        
        {error && (
            <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-10 text-center text-gray-500">Уншиж байна...</div>
          ) : (
            <SmartTable
              data={filteredRows}
              columns={columns}
              fetchData={fetchRows}
              settingsKey="summary-year"
              rowKey="Oid"
              editable={false}
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
              footerData={footerData}
            />
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default SummaryYearPage;
